import { h, Component } from 'preact';
import { AssetType } from '../../../typing/enums';
import { withContext } from '../../../common/context';
import { prepareCloudinaryParams, getBaseTransformation } from '../../../utils/cloudinary';
class CldVideoPlayer extends Component {
  static IGNORE_BASE_TRANSFORMATION_SOURCE_TYPES = ['hls', 'dash'];

  video;

  cldPlayer;

  shouldInitAutoPlay = false;

  componentDidMount() {
    if (this.getShowVideoTag()) {
      this.init();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.getShowVideoTag()) {
      return;
    }

    if (this.props.isReady) {
      // The logic of play/pause the video on first loaded should be done be the cld video player.
      this.shouldInitAutoPlay && this.props.shouldAutoPlay(prevProps, this.cldPlayer);
      this.shouldInitAutoPlay = true;
    }

    if (this.getShowVideoTag(prevProps) !== this.getShowVideoTag()) {
      this.init();
    }

    if (this.props.isReady && prevProps.publicId !== this.props.publicId) {
      this.setSource();
    }

    if (this.props.isReady && prevProps.active !== this.props.active) {
      if (!this.props.active) {
        this.cldPlayer.stop();
      } else if (this.videoProps.autoplay) {
        this.cldPlayer.play();
      }
    }
  }

  componentWillUnmount() {
    this.cldPlayer && this.cldPlayer.dispose();
  }

  getPlayerConfig() {
    return {
      ...this.videoProps,
      ...prepareCloudinaryParams(this.props.context.config.selectConfig()),
      controlBar: this.props.isNativeControls ? { pictureInPictureToggle: false } : false,
      controls: this.props.isControls,
      bigPlayButton:
        this.videoProps.bigPlayButton === undefined
          ? this.props.isMinimalControls
          : this.videoProps.bigPlayButton,
      loop: this.props.isLoop,
      muted: this.props.isMuted,
      autoplay: this.props.isAutoPlay,
      analytics: this.isAnalytics
    };
  }

  init() {
    this.cldPlayer && this.cldPlayer.dispose();

    const { context } = this.props;
    const cloudinary = context.cloudinary.getCore();

    this.cldPlayer = cloudinary.videoPlayer(this.video, this.getPlayerConfig());

    this.cldPlayer.on('ready', this.onReady);
    this.setSource();
  }

  onReady = () => {
    this.props.onReady(true);
    const isLoop = this.cldPlayer.loop();
    const controls = this.cldPlayer.controls();

    this.cldPlayer.on('play', this.props.onPlay);

    this.cldPlayer.on('pause', this.props.onPause);

    this.cldPlayer.on('ended', () => {
      if (controls && !isLoop) {
        this.props.onTimeProgress(100);
      }
    });

    this.cldPlayer.on('percentsplayed', event => {
      this.props.onTimeProgress(event.eventData.percent);
    });
  };

  setSource() {
    const {
      width,
      height,
      transformation,
      videoPlayerSource = {},
      assetTransformation
    } = this.props;

    /*
      It is not possible to make 'on the fly' transformations for long videos - these should be eagerly transcoded in advance.
      It's true for all type of videos.
      We ignore the base transformations for adaptive bitrate videos so we won't add transformations that have not been requested by the user.
      ToDo:
       * Find a solution for all long videos, not only for adaptive bitrate videos.
       * Find a better way to prevent added transformations
    */
    const ignoreBaseTransformation = new RegExp(
      CldVideoPlayer.IGNORE_BASE_TRANSFORMATION_SOURCE_TYPES.join('|')
    ).test(videoPlayerSource.sourceTypes);

    videoPlayerSource.transformation = Object.assign(
      {},
      ignoreBaseTransformation // ignore base transformation for adaptive bitrate videos
        ? assetTransformation
        : getBaseTransformation(width, height, AssetType.VIDEO, transformation),
      Array.isArray(videoPlayerSource.transformation)
        ? { transformation: videoPlayerSource.transformation }
        : videoPlayerSource.transformation
    );

    this.cldPlayer.source(this.props.publicId, videoPlayerSource);
  }

  get videoProps() {
    return this.props.context.config.selectConfig().videoProps;
  }

  get isAnalytics() {
    return this.props.context.config.selectConfig().analytics;
  }

  getShowVideoTag(props = this.props) {
    return props.preload || props.active || props.isReady;
  }

  render(props) {
    return (
      <div data-vjs-player>
        {this.getShowVideoTag() && (
          <video
            className="cld-video-player vjs-fill"
            ref={elem => (this.video = elem)}
            width={props.width}
            height={props.height}
            aria-label={props.ariaLabel}
          />
        )}
        {!props.isReady && this.props.loadingComponent()}
      </div>
    );
  }
}

export default withContext(CldVideoPlayer);
